import { ROUNDING_MODE_DOWN, ROUNDING_MODE_UP } from "../constants/paymentPlanRoundingMode";
import { SURCHARGE_TYPE_FIXED_PER_INSTALLMENT, SURCHARGE_TYPE_PERCENT_OF_TOTAL } from "../constants/paymentPlanSurchargeType";

/**
 * Vorschau der Ratenzahlung — Aufpreis und Rundung nach Zahlplan.
 *
 * **Anzeige-Kopie, nicht maßgeblich.** Gerechnet und gespeichert wird im Backend
 * (`Services/InstallmentCalculator.cs`, aufgerufen aus `ContractPackagesController.CreateCore`
 * und `ContributionCalendarProcessor`). Diese Fassung existiert nur, damit der Ratenbetrag im
 * Formular sichtbar ist, bevor gespeichert wird.
 *
 * Sie stand vorher **zweimal** im Code — einmal in `personDetail/personDetailUtils.ts`, einmal
 * lokal in `pages/sales/contractInstallmentPreview.ts`. Beide Fassungen ließen den Aufpreis bei
 * genau einer Rate aus. `paymentPlanInstallments.test.ts` hält die Vorschau gegen die Backend-Regel.
 */

const PERCENT_SCALE = 100;
const ROUND_HALF_UP_THRESHOLD = 0.5;

export type PaymentPlanRoundingInput = {
  surchargeType?: number | null;
  surchargeValue?: number | null;
  roundingMode?: number | null;
  roundingStep?: number | null;
  roundingEnding?: number | null;
};

/** Kaufmännisch auf zwei Nachkommastellen — wie `Math.Round(x, 2, MidpointRounding.AwayFromZero)`. */
export function round2(value: number): number {
  const v = Number(value || 0);
  return Math.round(v * PERCENT_SCALE) / PERCENT_SCALE;
}

/** Entspricht `InstallmentCalculator.ApplyRounding`. */
export function applyPaymentPlanRounding(
  value: number,
  roundingMode?: number | null,
  roundingStep?: number | null,
  roundingEnding?: number | null
): number {
  const mode = Number(roundingMode || 0);
  const step = roundingStep != null ? Number(roundingStep) : null;
  if (!mode || !step || step <= 0) return round2(value);

  const ending = roundingEnding != null ? Number(roundingEnding) : 0;
  const shifted = Math.max(0, Number(value || 0) - ending);
  const div = shifted / step;

  let q: number;
  if (mode === ROUNDING_MODE_DOWN) q = Math.floor(div);
  else if (mode === ROUNDING_MODE_UP) q = Math.ceil(div);
  else {
    // Nearest, halbe Schritte nach oben — `MidpointRounding.AwayFromZero` bei stets positivem `div`.
    const fl = Math.floor(div);
    q = div - fl >= ROUND_HALF_UP_THRESHOLD ? fl + 1 : fl;
  }

  return round2(Math.max(0, q * step + ending));
}

/**
 * Entspricht `InstallmentCalculator.Calculate`: Aufpreis auf den Grundbetrag, Teilung durch die
 * Ratenzahl, Rundung der Rate, Gesamtbetrag aus der gerundeten Rate.
 *
 * Bei genau einer Rate bleibt der Ursprungsbeitrag unverändert; der Aufpreis gilt erst bei
 * tatsächlicher Ratenzahlung mit mindestens zwei Raten.
 */
export function computeInstallmentsPreviewWithRounding(
  plan: PaymentPlanRoundingInput | null,
  basePrice: number,
  installmentCount: number
): { total: number; installment: number; count: number } {
  const type = Number(plan?.surchargeType || SURCHARGE_TYPE_FIXED_PER_INSTALLMENT);
  const surcharge = Number(plan?.surchargeValue || 0);
  let count = Number(installmentCount || 1);
  if (!Number.isFinite(count) || count < 1) count = 1;

  const base = Number(basePrice || 0);
  let totalExact = base;
  if (count > 1 && surcharge > 0) {
    if (type === SURCHARGE_TYPE_FIXED_PER_INSTALLMENT) totalExact = base + surcharge * count;
    else if (type === SURCHARGE_TYPE_PERCENT_OF_TOTAL) totalExact = base * (1 + surcharge / PERCENT_SCALE);
  }

  const installment = applyPaymentPlanRounding(
    totalExact / count,
    plan?.roundingMode,
    plan?.roundingStep,
    plan?.roundingEnding
  );
  return { total: round2(installment * count), installment, count };
}
